import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import {
  Form,
  InputField,
  PhoneNumberField,
  StateField,
  CountryField,
} from "@mimber/forms"
import { useCreateOrganization } from "@mimber/client/mimber-organization"
import { setSelectedOrganizationId } from "@/utils/organization"
import type { CreateOrganizationFormProps } from "./CreateOrganizationForm.types"
import {
  createOrganizationSchema,
  type CreateOrganizationData,
} from "./CreateOrganizationForm.schema"

export const CreateOrganizationForm: React.FunctionComponent<
  CreateOrganizationFormProps
> = ({ onSuccess }) => {
  const form = useForm<CreateOrganizationData>({
    resolver: zodResolver(createOrganizationSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      website: "",
      address: "",
      city: "",
      state: "",
      country: "",
      phoneNumber: "",
      email: "",
    },
  })

  const {
  } = form

  const mutation = useCreateOrganization({
    onSuccess: (response) => {
      setSelectedOrganizationId(response.data.uuid)
      message.success("Organization created successfully")
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to create organization: ${error.message}`)
    },
  })

  const onSubmit = (data: CreateOrganizationData) => {
    // Add ownerId (backend will override with current user) and pass the data
    const cleanData = {
      ...data,
      ownerId: "", // Backend will override with current user
    }
    mutation.mutate(cleanData)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Organization Name"
        placeholder="Enter organization name"
        required
      />
      <InputField
        name="description"
        label="Description"
        placeholder="Brief description of the organization"
      />
      <InputField
        name="website"
        label="Website"
        placeholder="https://example.com"
      />
      <InputField
        name="email"
        label="Email"
        placeholder="contact@example.com"
      />
      <PhoneNumberField
        name="phoneNumber"
        label="Phone Number"
        placeholder="+1 (555) 123-4567"
      />
      <InputField name="address" label="Address" placeholder="Street address" />
      <InputField name="city" label="City" placeholder="City" />
      <StateField name="state" label="State/Province" />
      <CountryField name="country" label="Country" />
      <Space style={{ marginTop: 16 }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}

          block
        >
          Create Organization
        </Button>
      </Space>
    </Form>
  )
}
